import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { installmentRemaining, type SaleInstallmentStatus } from "@/lib/sales/saleInstallments";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type InstallmentPaymentTarget = {
  id: number;
  installment_no: number;
  due_date: string;
  amount: number;
  paid_amount: number;
  remaining_amount?: number;
  status: SaleInstallmentStatus;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  installment: InstallmentPaymentTarget | null;
  saleOutstanding?: number | null;
  onSubmit: (payload: { amount: number; payment_date: string }) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function RecordInstallmentPaymentDialog({
  open,
  onOpenChange,
  installment,
  saleOutstanding,
  onSubmit,
}: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = useMemo(
    () => (installment ? installmentRemaining(installment) : 0),
    [installment],
  );

  const maxPayable = useMemo(() => {
    if (saleOutstanding != null && Number.isFinite(Number(saleOutstanding))) {
      return Math.max(0, Math.round(Number(saleOutstanding) * 100) / 100);
    }
    return remaining;
  }, [remaining, saleOutstanding]);

  useEffect(() => {
    if (!open || !installment) return;
    setAmountInput(String(remaining));
    const dueDate = installment.due_date?.trim();
    setPaymentDate(dueDate || todayIso());
    setSaving(false);
    setError(null);
  }, [open, installment, remaining]);

  const save = async () => {
    if (!installment || saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError("Enter a valid payment amount greater than zero.");
      return;
    }
    if (amount > maxPayable + 0.009) {
      setError(`Payment cannot exceed the outstanding balance of ${maxPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Payment date is required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({ amount, payment_date: paymentDate });
      onOpenChange(false);
    } catch {
      // Caller shows toast; keep dialog open for correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            {installment
              ? `Installment #${installment.installment_no} · ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining of ${installment.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "Enter payment details."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="installment-payment-amount">Payment amount</Label>
            <Input
              id="installment-payment-amount"
              type="text"
              inputMode="decimal"
              value={amountInput}
              disabled={saving}
              onChange={(e) => {
                setAmountInput(e.target.value);
                setError(null);
              }}
              placeholder="0.00"
            />
            <p className="text-xs text-muted-foreground">
              Defaults to the remaining balance. Overpayments apply to later installments automatically.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Payment date</Label>
            <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={saving || !installment}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Record payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
